import { api } from './client';

const DEVICE_ID_KEY = 'khiimori-sync-device-id';

export function getOrCreateDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

export function detectPlatform(): string {
  const ua = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  if (/android/.test(ua)) return 'android';
  return 'web';
}

export function detectDeviceName(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android Device';
  if (/Mac/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows PC';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Web Browser';
}

export async function registerCurrentDevice(): Promise<void> {
  await api.post('/cross-platform/devices', {
    id: getOrCreateDeviceId(),
    name: detectDeviceName(),
    type: /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
    platform: detectPlatform(),
  });
}

export interface SyncSettings {
  auto_sync: boolean;
  sync_interval: number;
  wifi_only: boolean;
  sync_on_startup: boolean;
  sync_on_mobile_data: boolean;
  conflict_resolution: string;
  sync_frequency: string;
}

export async function fetchSyncSettings(): Promise<SyncSettings> {
  const { data } = await api.get<{ settings: SyncSettings }>('/sync/settings');
  return data.settings;
}

export async function updateSyncSettings(settings: Partial<SyncSettings>): Promise<SyncSettings> {
  const { data } = await api.put<{ settings: SyncSettings }>('/sync/settings', settings);
  return data.settings;
}

export interface OfflineResource {
  id: string;
  url: string;
  size: string;
  cached_at: string;
  expires_at: string;
  type: string;
}

export interface OfflineQueueItem {
  id: string;
  type: string;
  entity: string;
  entity_id: string;
  data: Record<string, unknown>;
  created_at: string;
}

export async function fetchOfflineStorage() {
  const { data } = await api.get<{
    storage_used: string;
    storage_limit: string;
    pending_actions: number;
    total_items: number;
  }>('/offline/storage');
  return data;
}

export async function fetchOfflineCache(): Promise<OfflineResource[]> {
  const { data } = await api.get<{ resources: OfflineResource[] }>('/offline/cache');
  return data.resources ?? [];
}

export async function fetchOfflineQueue(): Promise<OfflineQueueItem[]> {
  const { data } = await api.get<{ queue: OfflineQueueItem[] }>('/offline/queue');
  return data.queue ?? [];
}

export async function syncOfflineQueue() {
  const { data } = await api.post('/offline/sync');
  return data;
}

export async function clearOfflineCache() {
  await api.delete('/offline/cache');
}

export async function deleteOfflineCacheItem(id: string) {
  await api.delete(`/offline/cache/${encodeURIComponent(id)}`);
}

export interface PWASettings {
  install_prompt: boolean;
  auto_update: boolean;
  notification_badge: boolean;
  background_sync: boolean;
  offline_pages: string[];
  theme_color: string;
  display_mode: string;
  orientation: string;
  version?: string;
  installable?: boolean;
}

export async function fetchPWASettings(): Promise<PWASettings> {
  const { data } = await api.get<{ settings: PWASettings }>('/pwa/settings');
  return data.settings;
}

export async function updatePWASettings(settings: Partial<PWASettings>): Promise<PWASettings> {
  const { data } = await api.put<{ settings: PWASettings }>('/pwa/settings', settings);
  return data.settings;
}

export async function fetchPWAStatus() {
  const { data } = await api.get<{ version: string; installable: boolean; update_available: boolean }>('/pwa/status');
  return data;
}

export interface MobileShareSession {
  id: string;
  name: string;
  tab_count: number;
  shared_with: string[];
  created_at: string;
  expires_at: string;
  status: string;
  url?: string;
  code?: string;
}

export async function fetchMobileShareSessions(): Promise<MobileShareSession[]> {
  const { data } = await api.get<{ sessions: MobileShareSession[] }>('/mobile-share/sessions');
  return data.sessions ?? [];
}

export async function createMobileShareLink(entityType = 'session', entityId?: string) {
  const { data } = await api.post('/mobile-share/links', {
    entity_type: entityType,
    entity_id: entityId ?? crypto.randomUUID(),
  });
  return data as { id: string; url: string; code: string; expires_at: string };
}

export async function fetchMobileShareContacts() {
  const { data } = await api.get<{ contacts: Array<{ name: string; avatar: string; last_shared: string }> }>(
    '/mobile-share/contacts',
  );
  return data.contacts ?? [];
}

export async function fetchMobileShareStats() {
  const { data } = await api.get<{
    total_shares: number;
    active_shares: number;
    total_tabs_shared: number;
    recipients: number;
    this_week: number;
  }>('/mobile-share/stats');
  return data;
}

export interface MobileCompanionDevice {
  id: string;
  name: string;
  type?: string;
  platform?: string;
  is_online?: boolean;
  last_seen?: string;
  last_active?: string;
  sync_enabled?: boolean;
  battery_level?: number;
}

export async function fetchMobileCompanionDevices(): Promise<MobileCompanionDevice[]> {
  const { data } = await api.get<{ devices: MobileCompanionDevice[] }>('/mobile-companion/devices');
  return data.devices ?? [];
}

export async function registerMobileCompanionDevice(name: string, type = 'phone') {
  const { data } = await api.post('/mobile-companion/devices', { name, type });
  return data;
}

export async function fetchMobileCompanionStats() {
  const { data } = await api.get<{
    total_devices: number;
    sync_active: number;
    last_sync: string;
    mobile_sessions: number;
  }>('/mobile-companion/stats');
  return data;
}

export async function syncMobileCompanion() {
  const { data } = await api.post('/mobile-companion/sync');
  return data;
}

export async function sendMobileHandoff(deviceId: string, tabId?: string) {
  const { data } = await api.post('/mobile-companion/handoff', { device_id: deviceId, tab_id: tabId });
  return data;
}
